import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import SpendingBar from '../components/ui/SpendingBar';
import { PolicySwitch } from '../components/accountModels/PolicySwitch';
import { useAccountPlan, useAccountUsage } from '../hooks/useAccountUsage';
import { useUpdateAccountSpending } from '../hooks/useAccountSpending';
import { useFlag } from '../hooks/useFeatureFlags';
import { useToast } from '../contexts/ToastContext';
import { refusalReason } from '../utils/apiRefusal';
import {
  DEFAULT_ALERT_THRESHOLDS_PCT,
  MAX_ALERT_THRESHOLDS,
  SPENDING_TEXT_CLASSES,
  formatAlertTime,
  formatBilledCents,
  formatSpendingCents,
  formatSpendingMonth,
  spendingReading,
  type SpendingReading,
} from '../utils/spending';
import type { AccountSpending } from '../types/billing';

/**
 * Settings › Billing › Spending (task 1159).
 *
 * Report: `project/reports/innovate-account-spending-limit-and-alerts.md`.
 * Mockup: `project/artifacts/innovate-app-settings-billing-spending-mockup.html`
 * (frame 1 is the main state, frame 2 the other states).
 *
 * One card that reads top to bottom: the reading (this month's billed usage
 * cost against the limit, the bar with the thresholds marked, the alerts sent),
 * then the three controls (monthly limit, alert thresholds, stop external calls
 * at the limit), then the save row. The mockup's audit line ("Last changed by
 * … · …") is not drawn: task 1156 records no who/when for the setting. "What counts" sits
 * under the card so the figure is never ambiguous.
 *
 * Reads come from the usage summary's `spending` block (service task 1156); the
 * write is `PATCH /api/accounts/spending`. The route and sidebar entry are
 * gated by `innovate.fe.spending.page` (owner-only cohort in v1). Within the
 * page, the controls follow `innovate.fe.account.spending` (task 1217, split
 * off `innovate.fe.account.manage`; owners and admins,
 * the service's own write rule), so when the page flag admits admins or
 * members later, a member already sees the reading only.
 *
 * Free plan: the plan cannot serve AI Services outside the app, so there is
 * nothing to measure. The page says so and shows no reading or bar; the
 * controls stay visible with Save disabled (the task's rule).
 */

/** Dollars typed by the User → cents, or `null` for an empty field. `NaN` = unreadable. */
function parseDollars(value: string): number | null {
  const trimmed = value.trim().replace(/^\$/, '').replace(/,/g, '');
  if (trimmed === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return NaN;
  return Math.round(Number(trimmed) * 100);
}

function centsToField(cents: number | null | undefined): string {
  return cents && cents > 0 ? (cents / 100).toFixed(2) : '';
}

/** Validate the thresholds before save: 1–5 whole numbers, 1–100, distinct; returned sorted. */
function checkThresholds(values: number[]): { sorted: number[]; error: string | null } {
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 0) return { sorted, error: 'Add at least one alert threshold.' };
  if (sorted.length > MAX_ALERT_THRESHOLDS) {
    return { sorted, error: `Use at most ${MAX_ALERT_THRESHOLDS} alert thresholds.` };
  }
  if (sorted.some((v) => !Number.isInteger(v) || v < 1 || v > 100)) {
    return { sorted, error: 'Each alert threshold is a whole percentage between 1% and 100%.' };
  }
  if (new Set(sorted).size !== sorted.length) return { sorted, error: 'Each alert threshold can appear only once.' };
  return { sorted, error: null };
}

function cardBadge(reading: SpendingReading): { label: string; className: string } | null {
  if (!reading.hasLimit) return null;
  if (reading.stopped) {
    return { label: 'external calls stopped', className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' };
  }
  if (reading.crossed && reading.highestAlertPct !== null) {
    return {
      label: `${reading.highestAlertPct}% alert sent`,
      className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    };
  }
  return { label: 'alerts armed', className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200' };
}

function AlertHistory({ spending, reading }: { spending: AccountSpending; reading: SpendingReading }) {
  const sent = [...spending.alertsSent].sort((a, b) => a.thresholdPct - b.thresholdPct);
  const sentPcts = new Set(sent.map((a) => a.thresholdPct));
  const pending = spending.alertThresholdsPct.filter((pct) => !sentPcts.has(pct));

  if (sent.length === 0) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="spending-alerts-none">
        Alerts: none sent this month · {spending.alertThresholdsPct.map((p) => `${p}%`).join(', ')} armed
      </p>
    );
  }

  return (
    <ul className="space-y-1 text-xs" data-testid="spending-alert-history">
      {sent.map((alert) => (
        <li key={alert.thresholdPct} data-testid={`spending-alert-sent-${alert.thresholdPct}`} className="text-gray-700 dark:text-gray-300">
          <span className="font-semibold">{alert.thresholdPct}% alert sent on {formatAlertTime(alert.sentAt)}</span>
          {alert.thresholdPct >= 100 && (
            <span className="text-gray-500 dark:text-gray-400">
              {reading.stopped ? ' · external calls are stopped' : ' · calls continue, usage is billed'}
            </span>
          )}
        </li>
      ))}
      {pending.map((pct) => (
        <li key={pct} className="text-gray-500 dark:text-gray-400">
          {pct}% alert not yet reached
        </li>
      ))}
    </ul>
  );
}

export default function SettingsSpendingPage() {
  // The spending limit split off innovate.fe.account.manage (task 1217), same admin+ cohorts.
  const canManage = useFlag('innovate.fe.account.spending');
  const toast = useToast();
  const { data: usage, isLoading } = useAccountUsage();
  const { plan, limits } = useAccountPlan();
  const update = useUpdateAccountSpending();

  const spending: AccountSpending = useMemo(
    () => ({
      limitCents: usage?.spending?.limitCents ?? null,
      hardStop: usage?.spending?.hardStop ?? false,
      // Null only when the service could not resolve the plan's markup.
      billedCents: usage?.spending ? usage.spending.billedCents : 0,
      alertThresholdsPct:
        usage?.spending?.alertThresholdsPct && usage.spending.alertThresholdsPct.length > 0
          ? usage.spending.alertThresholdsPct
          : DEFAULT_ALERT_THRESHOLDS_PCT,
      alertsSent: usage?.spending?.alertsSent ?? [],
      stopped: usage?.spending?.stopped ?? false,
    }),
    [usage?.spending]
  );
  const reading = spendingReading(spending);
  const isFreePlan = plan?.toLowerCase() === 'free';
  const markupBps = limits['model_cost_markup_bps'];
  const markup = typeof markupBps === 'number' && markupBps >= 0 ? markupBps / 10_000 : null;
  const monthLabel = formatSpendingMonth(usage?.period) ?? 'This month';

  // --- Controls: a draft of the saved setting -------------------------------
  const [limitField, setLimitField] = useState('');
  const [thresholds, setThresholds] = useState<number[]>(DEFAULT_ALERT_THRESHOLDS_PCT);
  const [hardStop, setHardStop] = useState(false);
  const [newThreshold, setNewThreshold] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const resetDraft = () => {
    setLimitField(centsToField(spending.limitCents));
    setThresholds(spending.alertThresholdsPct);
    setHardStop(spending.hardStop);
    setNewThreshold('');
    setFormError(null);
  };

  // Load the saved setting into the draft whenever the summary changes.
  useEffect(() => {
    resetDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spending.limitCents, spending.hardStop, spending.alertThresholdsPct.join(',')]);

  const addThreshold = () => {
    const value = Number(newThreshold);
    if (!/^\d+$/.test(newThreshold.trim()) || value < 1 || value > 100) {
      setFormError('Each alert threshold is a whole percentage between 1% and 100%.');
      return;
    }
    if (thresholds.includes(value)) {
      setFormError(`${value}% is already an alert threshold.`);
      return;
    }
    if (thresholds.length >= MAX_ALERT_THRESHOLDS) {
      setFormError(`Use at most ${MAX_ALERT_THRESHOLDS} alert thresholds.`);
      return;
    }
    setThresholds([...thresholds, value].sort((a, b) => a - b));
    setNewThreshold('');
    setFormError(null);
  };

  const save = async () => {
    const limitCents = parseDollars(limitField);
    if (Number.isNaN(limitCents) || (limitCents !== null && limitCents <= 0)) {
      setFormError('Enter the monthly limit in dollars, like 200 or 200.00, or clear it to remove the limit.');
      return;
    }
    const { sorted, error } = checkThresholds(thresholds);
    if (error) {
      setFormError(error);
      return;
    }
    setFormError(null);
    try {
      await update.mutateAsync({ limitCents, alertThresholdsPct: sorted, hardStop });
      toast.success(limitCents === null ? 'Spending limit removed' : 'Spending limit saved');
    } catch (err) {
      toast.error('Could not save the spending limit', refusalReason(err));
    }
  };

  const badge = cardBadge(reading);

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Spending" />
        <p className="mt-6 text-sm text-gray-500 dark:text-gray-400" data-testid="spending-loading">
          Loading spending…
        </p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Spending"
        description="A monthly limit on usage cost, alerts as you approach it, and an optional stop on external calls when you reach it."
      />

      <div className="mt-6 space-y-6">
        <section
          className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
          data-testid="spending-card"
          data-state={isFreePlan ? 'free' : !reading.hasLimit ? 'no-limit' : reading.tone}
        >
          {isFreePlan ? (
            <div data-testid="spending-free-plan-note">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Nothing to measure yet</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Your plan cannot serve AI Services outside the app, so there is no usage cost to limit yet. Runs your
                team makes in the app are included in your plan. Choose a paid plan on{' '}
                <Link to="/settings/subscription" className="font-medium text-indigo-600 hover:underline dark:text-indigo-400">
                  Subscription
                </Link>{' '}
                to deploy externally; a limit can be set here from then on.
              </p>
            </div>
          ) : (
            <div data-testid="spending-reading">
              <h2 className="flex flex-wrap items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
                {monthLabel}
                {badge && (
                  <span
                    data-testid="spending-badge"
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                  >
                    {badge.label}
                  </span>
                )}
              </h2>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Usage cost so far, measured the same way the Billing page and your invoice measure it
                {markup !== null ? `: Model cost × your plan's ${markup.toFixed(2)} markup.` : '.'}
              </p>

              <div className="mt-3 flex flex-wrap items-baseline gap-2" data-testid="spending-figure">
                <span className="text-2xl font-bold tabular-nums text-gray-900 dark:text-white">
                  {formatBilledCents(reading.billedCents)}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {reading.limitCents !== null
                    ? `of ${formatSpendingCents(reading.limitCents)} monthly limit`
                    : 'no monthly limit'}
                </span>
                {reading.pct !== null && (
                  <span
                    className={`ml-auto text-sm font-semibold tabular-nums ${SPENDING_TEXT_CLASSES[reading.tone]}`}
                    data-testid="spending-pct"
                  >
                    {reading.pct}%
                  </span>
                )}
              </div>

              {reading.hasLimit ? (
                <div className="mt-2 space-y-3">
                  <SpendingBar reading={reading} thresholdsPct={spending.alertThresholdsPct} />
                  <AlertHistory spending={spending} reading={reading} />
                </div>
              ) : (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300" data-testid="spending-no-limit-prompt">
                  Set a limit to get alerts as usage grows, and optionally to stop external calls when it is reached.
                  Until then usage is billed exactly as today.
                </p>
              )}
            </div>
          )}

          {canManage ? (
            <div className="mt-5 border-t border-gray-200 pt-5 dark:border-gray-700" data-testid="spending-controls">
              <div className="grid gap-5 md:grid-cols-3">
                <div>
                  <label htmlFor="spending-limit" className="block text-sm font-medium text-gray-900 dark:text-white">
                    Monthly limit
                  </label>
                  <div className="mt-1 flex items-center rounded-md border border-gray-300 bg-white px-2 dark:border-gray-600 dark:bg-gray-900">
                    <span className="text-sm text-gray-500 dark:text-gray-400">$</span>
                    <input
                      id="spending-limit"
                      type="text"
                      inputMode="decimal"
                      value={limitField}
                      onChange={(e) => setLimitField(e.target.value)}
                      placeholder="no limit"
                      data-testid="spending-limit-input"
                      className="w-full border-0 bg-transparent px-1 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-0 dark:text-white"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Billed usage cost per calendar month (UTC). Clear the amount to remove the limit.
                  </p>
                </div>

                <div>
                  <span className="block text-sm font-medium text-gray-900 dark:text-white">Alert at</span>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5" data-testid="spending-thresholds">
                    {thresholds.map((pct) => (
                      <span
                        key={pct}
                        data-testid={`spending-threshold-${pct}`}
                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                      >
                        {pct}%
                        <button
                          type="button"
                          aria-label={`Remove the ${pct}% alert`}
                          onClick={() => setThresholds(thresholds.filter((v) => v !== pct))}
                          className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    {thresholds.length < MAX_ALERT_THRESHOLDS && (
                      <span className="inline-flex items-center gap-1">
                        <input
                          type="text"
                          inputMode="numeric"
                          aria-label="New alert threshold percentage"
                          value={newThreshold}
                          onChange={(e) => setNewThreshold(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addThreshold();
                            }
                          }}
                          placeholder="%"
                          data-testid="spending-threshold-input"
                          className="w-12 rounded-md border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                        />
                        <button
                          type="button"
                          onClick={addThreshold}
                          data-testid="spending-threshold-add"
                          className="text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                        >
                          + Add threshold
                        </button>
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Each threshold emails every owner and admin once per month. Up to five, between 1% and 100%.
                  </p>
                </div>

                <div>
                  <span className="block text-sm font-medium text-gray-900 dark:text-white">At the limit</span>
                  <div className="mt-1 flex items-start gap-3">
                    <PolicySwitch
                      checked={hardStop}
                      onToggle={() => setHardStop(!hardStop)}
                      label="Stop external calls at the limit"
                      testId="spending-hard-stop"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Stop external calls at the limit</p>
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        Off: the limit alerts only, and usage keeps being billed. On: public, embed, API-key, scheduled
                        and message-route calls to this account&apos;s AI Services are refused once the limit is reached,
                        until the month rolls over or you raise the limit. Your own team&apos;s runs in the app are never
                        stopped.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {formError && (
                <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert" data-testid="spending-form-error">
                  {formError}
                </p>
              )}

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={save}
                  disabled={isFreePlan || update.isPending}
                  title={isFreePlan ? 'A limit can be set once the account is on a paid plan.' : undefined}
                  data-testid="spending-save"
                  className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {update.isPending ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={resetDraft}
                  data-testid="spending-cancel"
                  className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700/50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-300" data-testid="spending-read-only">
              The spending limit is managed by your account owner or an admin.
            </p>
          )}
        </section>

        <section
          className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
          data-testid="spending-what-counts"
        >
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">What counts toward the limit</h2>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">Counted</h3>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-gray-600 dark:text-gray-300">
                <li>Public calls and embeds of your AI Services</li>
                <li>API-key calls</li>
                <li>Scheduled runs</li>
                <li>Message-route runs</li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">Not counted</h3>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-gray-600 dark:text-gray-300">
                <li>Runs your team makes in the app (not billed)</li>
                <li>Platform services such as the chat guide (not billed to you)</li>
                <li>Your plan fee and extra-endpoint fees (fixed, shown on Billing)</li>
                <li>Usage on your own provider keys (not billed by Innovate)</li>
              </ul>
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            A run&apos;s cost is known when it finishes, so the run that crosses the limit completes and is billed; the
            next one is refused. The limit is exact to within one run.
          </p>
        </section>
      </div>
    </div>
  );
}
